import { useId } from "react";
import { Check } from "lucide-react";
import Modal from "../elements/Modal";
import Button from "../ui/Button";
import "./PickerHeadsUp.css";

// Photo tiles in the drawn picker (3 x 3) and where the five checks sit on them.
const TILE_TINTS = ["#E7DCCF", "#DCE2D6", "#E6D9DB", "#E2DDD0", "#DCE2D6", "#E7DCCF", "#E6D9DB", "#E2DDD0", "#E7DCCF"];
const CHECKS = [
  [17, 25],
  [44, 25],
  [70, 25],
  [17, 51],
  [44, 51],
];

/**
 * The heads-up before the photo picker on iPhone and iPad (docs/design/HANDOFF.md,
 * "iPhone heads-up before the photo picker", `PopIphone`): after the blue check
 * mark the picker sits still while the device gets the photos ready, and looks
 * frozen. One looping animation (tap the check, wait, photos upload, select
 * photos) with a caption per step; screen readers get the same instructions as
 * a hidden title and description instead.
 *
 * TODO(design): iPads get the same pop-up with the phone drawing; there is no
 * tablet design yet (docs/design/GAPS.md, "Photo picker heads-up on iPad").
 */
export const PickerHeadsUp = ({ open, device = "iphone", accept = "image/*", onFiles, onClose }) => {
  const titleId = useId();
  const bodyId = useId();
  const name = device === "ipad" ? "iPad" : "iPhone";
  return (
    <Modal
      isOpen={open}
      setIsOpen={(next) => {
        if (!next) onClose?.();
      }}
      header={false}
      isClose={false}
      size="md"
      modalDialogueStyle="!rounded-[22px] md:!rounded-3xl"
      bodyClasses="!px-[18px] !pt-[26px] !pb-3.5"
    >
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId} className="flex flex-col gap-[18px]">
        <h2 id={titleId} className="sr-only">
          Your {name} needs a minute
        </h2>
        <p id={bodyId} className="sr-only">
          Select your photos, then tap the blue check mark once. After that, the screen won't change for a few seconds while your {name} gets
          your photos ready. Please wait and don't close the picker. Your photos will start adding when it closes.
        </p>

        <div aria-hidden="true" className="relative h-8 text-center font-heading text-[28px] leading-8 text-md-dark">
          <span className="phu-loop phu-cap-select absolute inset-0">Select your photos</span>
          <span className="phu-loop phu-cap-tap absolute inset-0">Tap the blue check once</span>
          <span className="phu-loop phu-cap-wait absolute inset-0">Give it a few seconds</span>
          <span className="phu-loop phu-cap-upload absolute inset-0">Your photos upload</span>
        </div>
        <div aria-hidden="true" className="relative -mt-3 h-[22px] text-center text-base leading-[22px] text-md-muted">
          <span className="phu-loop phu-cap-wait absolute inset-0">(It's not frozen)</span>
        </div>
        {/* Step bars in the order the steps are explained, so the second one is lit when the pop-up opens. */}
        <div aria-hidden="true" className="-mt-1.5 flex items-center justify-center gap-1.5">
          <span className="phu-loop phu-bar phu-bar-select" />
          <span className="phu-loop phu-bar phu-bar-tap" />
          <span className="phu-loop phu-bar phu-bar-wait" />
          <span className="phu-loop phu-bar phu-bar-upload" />
        </div>

        <div aria-hidden="true" className="flex w-full justify-center rounded-2xl bg-[#F9F3EC] px-3 pb-4 pt-[22px]">
          <div className="flex h-[250px] w-[260px] items-center justify-center">
            <div className="relative flex scale-[1.6] items-center">
              <div className="relative h-[150px] w-[96px] rounded-2xl bg-md-dark p-[5px]">
                <div className="relative h-full w-full overflow-hidden rounded-xl bg-white">
                  <div className="phu-loop phu-picker absolute inset-0">
                    <div className="flex h-5 items-center justify-end px-1.5">
                      <span className="phu-loop phu-done relative flex h-4 w-4 items-center justify-center rounded-full bg-[#8DBEF2]">
                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />
                        <span className="phu-loop phu-tap absolute -inset-1.5 rounded-full border-2 border-md-pink-dark" />
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-0.5 px-1 py-0.5">
                      {TILE_TINTS.map((tint, i) => (
                        <span key={i} className="h-6 rounded-sm" style={{ background: tint }} />
                      ))}
                    </div>
                    <div className="phu-loop phu-dim absolute inset-x-0 bottom-0 top-5 bg-white" />
                    {CHECKS.map(([left, top], i) => (
                      <span
                        key={i}
                        className={`phu-loop phu-check-${i} absolute flex h-[9px] w-[9px] items-center justify-center rounded-full border-[1.5px] border-white bg-[#0050C8]`}
                        style={{ left, top }}
                      >
                        <Check className="h-[5px] w-[5px] text-white" strokeWidth={5} />
                      </span>
                    ))}
                  </div>
                  <div className="phu-loop phu-adding absolute inset-0 flex flex-col justify-center bg-md-pink-light-2 p-2">
                    <div className="flex flex-col items-center gap-1 rounded-md border border-md-pink-light-1 bg-white p-1.5">
                      <span className="flex gap-[3px]">
                        <span className="phu-dot h-1 w-1 rounded-full bg-md-pink" />
                        <span className="phu-dot h-1 w-1 rounded-full bg-md-pink [animation-delay:.2s]" />
                        <span className="phu-dot h-1 w-1 rounded-full bg-md-pink [animation-delay:.4s]" />
                      </span>
                      <span className="text-center text-[8px] font-bold text-md-dark">Adding photos</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="phu-loop phu-clock absolute left-[calc(100%+2px)] top-1/2 -mt-[22px] h-11 w-11 rounded-full border-2 border-[#E5C5CB] bg-white">
                <span className="phu-hand absolute left-1/2 top-1/2 -ml-px h-[15px] w-0.5 bg-md-pink-dark" />
                <span className="absolute left-1/2 top-1/2 -ml-[3px] -mt-[3px] h-1.5 w-1.5 rounded-full bg-md-pink-dark" />
              </div>
            </div>
          </div>
        </div>

        <div className="-mt-1 flex flex-col gap-0.5">
          {/* The button is the file input itself, so iOS opens its Photo Library / Take Photo / Choose Files
              menu from this button with the pop-up still up. The pop-up closes when photos arrive; backing
              out of the menu or the picker leaves it open. */}
          <div className="relative">
            <input
              type="file"
              multiple
              accept={accept}
              aria-label="Choose my photos"
              className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = "";
                if (files.length) onFiles?.(files);
              }}
            />
            <Button size="lg" fullWidth tabIndex={-1} aria-hidden="true" className="pointer-events-none peer-focus-visible:ring-2 peer-focus-visible:ring-md-pink peer-focus-visible:ring-offset-2">
              Choose my photos
            </Button>
          </div>
          <Button variant="text" fullWidth className="!text-md-muted hover:!no-underline" onClick={onClose}>
            Not now
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PickerHeadsUp;
